import {
  cancelOnetime,
  createFreeGiftOnetime,
  createOnetime,
  delayOnetime,
  getOnetimes,
  updateOnetime,
} from '@/services/onetime'
import { defineStore } from 'pinia'
import { AsyncOperationState } from './lib/AsyncOperationState'
import { useProductImageStore } from './productImage'
import { useProductLinkStore } from './productLink'

import Bugsnag from '@bugsnag/js'
import { useNextOrderStore } from './nextOrder'

export interface Onetime {
  id: number
  address_id: number
  customer_id: number
  created_at: string
  external_product_id: {
    ecommerce: string
  }
  external_variant_id: {
    ecommerce: string
  }
  next_charge_scheduled_at: string
  presentment_currency: string
  price: string
  product_title: string
  properties: []
  quantity: number
  is_cancelled: boolean
  sku: string | null
  sku_override: boolean
  updated_at: string
  variant_title: string | null
}

export const useOnetimeStore = defineStore('onetime', {
  state: () => ({
    loadState: AsyncOperationState.IDLE,
    workState: AsyncOperationState.IDLE,
    onetimes: [] as Onetime[],
    stale: true as boolean,
  }),
  getters: {},
  actions: {
    async init(rechargeId: string) {
      if (this.stale || this.loadState === AsyncOperationState.IDLE) {
        await this.load(rechargeId)
      }
    },
    async load(rechargeId: string) {
      const productImageStore = useProductImageStore()
      const productLinkStore = useProductLinkStore()
      this.loadState = AsyncOperationState.IN_PROGRESS
      try {
        const onetimes = await getOnetimes(rechargeId)
        this.onetimes = onetimes
        productImageStore.loadImagesForProducts(
          this.onetimes.map(
            (sub) =>
              `${sub.external_product_id.ecommerce}:${sub.external_variant_id.ecommerce}`,
          ),
        )
        productLinkStore.loadLinksForProducts(
          this.onetimes.map((sub) => `${sub.external_product_id.ecommerce}`),
        )
        this.stale = false
        this.loadState = AsyncOperationState.DONE
      } catch (e) {
        this.loadState = AsyncOperationState.ERRORED
        Bugsnag.notify(e as Error)
      }
    },
    async cancelOnetime(rechargeId: string, onetimeId: number) {
      this.workState = AsyncOperationState.IN_PROGRESS
      try {
        await cancelOnetime(rechargeId, onetimeId)
        this.workState = AsyncOperationState.DONE
        const nextOrderStore = useNextOrderStore()
        nextOrderStore.stale = true
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async delayOnetime(rechargeId: string, onetimeId: number, until: string) {
      this.workState = AsyncOperationState.IN_PROGRESS
      const nextOrderStore = useNextOrderStore()
      try {
        const { onetime } = await delayOnetime(rechargeId, onetimeId, until)
        await this.patchOnetime(onetime)
        this.workState = AsyncOperationState.DONE
        nextOrderStore.stale = true
        return onetime
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async updateOnetime(
      rechargeId: string,
      onetimeId: number,
      update: {
        quantity: number
        newVariantId?: string
      },
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS
      const nextOrderStore = useNextOrderStore()
      try {
        const { onetime } = await updateOnetime(rechargeId, onetimeId, update)
        await this.patchOnetime(onetime)
        this.workState = AsyncOperationState.DONE
        nextOrderStore.stale = true
        return onetime
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async addToNextOrder(
      rechargeId: string,
      productId: string,
      variantId: string,
      quantity = 1,
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS
      try {
        const nextOrderStore = useNextOrderStore()
        // ensure the next order is loaded
        await nextOrderStore.init(rechargeId)
        const order = nextOrderStore.order
        if (order) {
          await createOnetime(
            rechargeId,
            order.address_id,
            productId,
            variantId,
            order.scheduled_at,
            quantity,
          )
          this.workState = AsyncOperationState.DONE
          const nextOrderStore = useNextOrderStore()
          nextOrderStore.stale = true
          this.stale = true
        } else {
          throw new Error('No next order found')
        }
      } catch (e) {
        this.workState = AsyncOperationState.ERRORED
        Bugsnag.notify(e as Error)
      }
    },
    async addFreeGiftToNextOrder(
      rechargeId: string,
      sourceProductId: string,
      variantId: string,
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS
      try {
        const nextOrderStore = useNextOrderStore()
        await nextOrderStore.init(rechargeId)
        const order = nextOrderStore.order
        if (!order) {
          throw new Error('No next order found')
        }
        await createFreeGiftOnetime(
          rechargeId,
          order.address_id,
          sourceProductId,
          variantId,
          order.scheduled_at,
        )
        this.workState = AsyncOperationState.DONE
        nextOrderStore.stale = true
        this.stale = true
      } catch (e) {
        this.workState = AsyncOperationState.ERRORED
        Bugsnag.notify(e as Error)
      }
    },
    async patchOnetime(onetime: Onetime) {
      const productImageStore = useProductImageStore()
      const productLinkStore = useProductLinkStore()
      this.$patch(() => {
        const onetimeIndex = this.onetimes.findIndex((s) => s.id === onetime.id)
        this.onetimes[onetimeIndex] = onetime
      })
      await productImageStore.loadImagesForProducts([
        `${onetime.external_product_id.ecommerce}:${onetime.external_variant_id.ecommerce}`,
      ])
      await productLinkStore.loadLinksForProducts([
        `${onetime.external_product_id.ecommerce}`,
      ])
    },
  },
})
